import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscribeModal({ tier, open, onClose }) {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [loading, setLoading] = useState(false);

  if (!open || !tier) return null;

  const labels = {
    nl: { title: "Abonneer op", name: "Naam", email: "E-mail", phone: "Telefoon",
          company: "Bedrijf", submit: "Doorgaan naar betalen", intro: "Je wordt doorgestuurd naar Mollie voor een eerste veilige betaling. Daarna start je maandelijkse abonnement automatisch.",
          perMonth: "/mnd" },
    en: { title: "Subscribe to", name: "Name", email: "Email", phone: "Phone",
          company: "Company", submit: "Continue to payment", intro: "You will be redirected to Mollie for a secure first payment. Your monthly subscription starts automatically after.",
          perMonth: "/mo" },
  }[lang] || {};

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscribe`, {
        tier: tier.key,
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        company: form.company || undefined,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("No checkout URL");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment setup failed");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="subscribe-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy-dark/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#F7F5F0] text-navy w-full max-w-lg border border-navy relative"
      >
        <button
          onClick={onClose}
          data-testid="subscribe-modal-close"
          className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full hover:bg-navy/5"
        >
          <X size={18} />
        </button>
        <div className="p-8 md:p-10">
          <div className="eyebrow text-ink mb-2">{labels.title}</div>
          <div className="flex items-baseline gap-2 mb-1">
            <h3 className="heading-display text-4xl md:text-5xl">{tier.name}</h3>
          </div>
          <div className="flex items-baseline gap-1 mb-6">
            <span className="heading-display text-3xl">{tier.price}</span>
            <span className="text-sm text-ink">{labels.perMonth}</span>
          </div>
          <p className="text-sm text-ink mb-8 leading-relaxed">{labels.intro}</p>

          <form onSubmit={submit} className="space-y-5" data-testid="subscribe-form">
            <Field label={labels.name} required testid="sub-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label={labels.email} required type="email" testid="sub-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <div className="grid grid-cols-2 gap-4">
              <Field label={labels.phone} testid="sub-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label={labels.company} testid="sub-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="subscribe-submit-btn"
              className="btn-editorial btn-editorial-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? "…" : labels.submit} →
            </button>
          </form>

          <p className="text-[10px] uppercase tracking-[0.2em] text-ink text-center mt-6">
            Secure payment · iDEAL · Bancontact · Card · via Mollie
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, type = "text", testid, value, onChange }) {
  return (
    <label className="flex flex-col">
      <span className="eyebrow text-ink mb-1">
        {label}
        {required && <span className="ml-1 text-navy">*</span>}
      </span>
      <input
        required={required}
        type={type}
        data-testid={`${testid}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-b border-navy/40 pb-2 focus:outline-none focus:border-navy"
      />
    </label>
  );
}
